import { PaymentProductPrice, PaymentSettings, PriceIntervalEnum } from "@finway-group/shared/lib/models"
import { TenantTierEnum } from "@finway-group/shared/lib/models/featureFlags/tenantTier.enum"
import { Button, Col, Modal, Row } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"

import { CubeXLSuccessIcon, LinesIcon, RocketIcon } from "Components/icons"
import { TenantTierPaidEnum } from "Components/icons/tierIcon"
import { useTenantTier } from "Shared/hooks/featureFlags.hooks"
import { getLanguageCode } from "Shared/hooks/i18n.hooks"
import { NotificationService } from "Shared/services"
import PaymentSettingsService, { PaymentProductUI } from "Shared/services/paymentSettings.service"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"

import PaymentSubscriptionUpdateCard from "./paymentSubscriptionUpdateCard"
import PaymentSubscriptionUpdateSelect from "./paymentSubscriptionUpdateSelect"
import PaymentSubscriptionUpdateTierCol from "./paymentSubscriptionUpdateTierCol"

interface PaymentSubscriptionUpdateModalProps {
    plan?: PaymentProductUI
    plans: Array<PaymentProductUI>
    intervalFilter?: PriceIntervalEnum
    isShowing: boolean
    onCancel: () => void
    onUpgraded: (updatedPaymentSettings?: PaymentSettings) => void
}

enum PaymentSubscriptionUpdateStep {
    /**
     * Tier OverView
     */
    TIER_SELECT,
    /**
     * Plan select
     */
    PLAN_SELECT,
    /**
     * Plan confirm
     */
    PLAN_CONFIRM,
    /**
     * Finish aka thank you message
     */
    UPDATE_FINISH,
}

interface PaymentSubscriptionUpdateSelection {
    plan: PaymentProductUI
    price: PaymentProductPrice
}

const PaymentSubscriptionUpdateModal = ({ plan, plans, intervalFilter, isShowing, onCancel, onUpgraded }: PaymentSubscriptionUpdateModalProps) => {
    const { t } = useTranslation()
    const tenantTier = useTenantTier()
    const locale = getLanguageCode()

    const initialStep = tenantTier !== TenantTierEnum.TIER_2 ? PaymentSubscriptionUpdateStep.TIER_SELECT : PaymentSubscriptionUpdateStep.PLAN_SELECT

    const [isUpgrading, setIsUpgrading] = useStateIfMounted(false)
    // skip TIER_SELECT step if customer is already on TIER_2
    const [step, setStep] = useStateIfMounted(initialStep)
    const [tierSelection, setTierSelection] = useStateIfMounted<TenantTierPaidEnum>(TenantTierEnum.TIER_2)
    const [planSelection, setPlanSelection] = useStateIfMounted<PaymentSubscriptionUpdateSelection | undefined>(undefined)
    const [filteredAndSortedPlans, setFilteredAndSortedPlans] = useStateIfMounted<Array<PaymentProductUI>>([])

    useEffect(() => {
        const filteredPlans = plans.filter((e) => {
            const isTierMatching = e.metadata.tier === tierSelection
            const units = plan?.metadata.units
            if (units) {
                // in case of misconfigured products
                try {
                    const eUnits = parseInt(e.metadata.units)
                    const pUnits = parseInt(units)
                    // if the tier is higher allow upgrade to the same amount of units for upsell
                    if (tenantTier === TenantTierEnum.TIER_1 && tierSelection === TenantTierEnum.TIER_2) return eUnits >= pUnits && isTierMatching
                    // match units and tier
                    return eUnits > pUnits && isTierMatching
                } catch (err) {
                    return false
                }
            }
            // match only tier
            return isTierMatching
        })
        filteredPlans.sort((a, b) => Number(b.metadata.units) - Number(a.metadata.units))
        setFilteredAndSortedPlans(filteredPlans)
    }, [plan, tierSelection])

    const onHide = () => {
        setTierSelection(TenantTierEnum.TIER_2)
        setStep(initialStep)
        onCancel()
    }

    const onGetAQuote = () => {
        window.open("mailto:support@finway.de")
    }

    const onTierSelected = (tier: TenantTierPaidEnum) => {
        setTierSelection(tier)
        setStep(PaymentSubscriptionUpdateStep.PLAN_SELECT)
    }

    const onUpgrade = (plan: PaymentProductUI, price: any) => {
        setPlanSelection({ plan, price })
        setStep(PaymentSubscriptionUpdateStep.PLAN_CONFIRM)
    }

    const onBackSelection = () => {
        setTierSelection(TenantTierEnum.TIER_2)
        setStep(PaymentSubscriptionUpdateStep.TIER_SELECT)
    }

    const onBackConfirm = () => {
        setPlanSelection(undefined)
        setStep(PaymentSubscriptionUpdateStep.PLAN_SELECT)
    }

    const onUpgradeConfirmation = () => {
        if (!planSelection) return
        setIsUpgrading(true)
        PaymentSettingsService.updateSubscription(planSelection.price.priceId)
            .then((updatedPaymentSettings) => {
                onUpgraded(updatedPaymentSettings)
                setStep(PaymentSubscriptionUpdateStep.UPDATE_FINISH)
            })
            .catch((err) => {
                NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
            })
            .finally(() => setIsUpgrading(false))
    }

    const renderPlanCard = (plan: PaymentProductUI, price: any) => {
        const isInPlanSelectStep = step === PaymentSubscriptionUpdateStep.PLAN_SELECT
        return (
            <PaymentSubscriptionUpdateCard
                key={plan.productId}
                spanIcon={isInPlanSelectStep ? 2 : 3}
                spanName={isInPlanSelectStep ? 9 : 11}
                name={plan.metadata[`${locale}_name`] || plan.name}
                unitPrefixText={t("input:payment_settings.up_to")}
                units={Number(plan.metadata.units)}
                isPriceAvailable={true}
                spanPrice={isInPlanSelectStep ? 7 : 10}
                price={price}
                isButtonVisible={isInPlanSelectStep}
                spanButton={6}
                buttonText={t("action:payment_settings.upgrade")}
                onClick={() => onUpgrade(plan, price)}
                isButtonDisabled={isUpgrading}
            />
        )
    }

    const getWidth = () => {
        switch (step) {
            case PaymentSubscriptionUpdateStep.TIER_SELECT:
                return 889
            case PaymentSubscriptionUpdateStep.PLAN_SELECT:
                return 600
            default:
                return undefined
        }
    }

    const renderFooter = () => {
        switch (step) {
            case PaymentSubscriptionUpdateStep.TIER_SELECT: {
                return [
                    <Button key="c" onClick={onHide}>
                        {t("action:cancel")}
                    </Button>,
                ]
            }
            case PaymentSubscriptionUpdateStep.PLAN_SELECT:
                return [
                    <Button key="b" loading={isUpgrading} onClick={onBackSelection}>
                        {t("action:back")}
                    </Button>,
                    <Button key="c" onClick={onHide}>
                        {t("action:cancel")}
                    </Button>,
                ]
            case PaymentSubscriptionUpdateStep.PLAN_CONFIRM:
                return [
                    <Button key="b" disabled={isUpgrading} onClick={onBackConfirm}>
                        {t("action:back")}
                    </Button>,
                    <Button key="s" loading={isUpgrading} onClick={onUpgradeConfirmation} icon={<RocketIcon />} className="btn-highlight-green-active">
                        {t("confirm:payment_settings.upgrade.confirm")}
                    </Button>,
                ]
            case PaymentSubscriptionUpdateStep.UPDATE_FINISH:
                return [
                    <Button key="c" className="ant-btn-primary" onClick={onHide}>
                        {t("action:payment_settings.back_to_finway")}
                    </Button>,
                ]
        }
    }

    const renderContent = () => {
        // Fail safe: Something not right w/ the backend
        if (plans.length === 0)
            return (
                <div className="flex flex-col items-center">
                    <h2>{t("label:payment_settings.billing_plans_missing")}</h2>
                </div>
            )

        switch (step) {
            case PaymentSubscriptionUpdateStep.TIER_SELECT: {
                return (
                    <Row justify="space-around">
                        <PaymentSubscriptionUpdateTierCol plans={plans} tier={TenantTierEnum.TIER_1} onClick={() => onTierSelected(TenantTierEnum.TIER_1)} />
                        <PaymentSubscriptionUpdateTierCol plans={plans} tier={TenantTierEnum.TIER_2} onClick={() => onTierSelected(TenantTierEnum.TIER_2)} />
                    </Row>
                )
            }
            case PaymentSubscriptionUpdateStep.PLAN_SELECT: {
                return (
                    <PaymentSubscriptionUpdateSelect
                        plan={plan}
                        sortedPlans={filteredAndSortedPlans}
                        intervalFilter={intervalFilter}
                        tierSelection={tierSelection}
                        onGetAQuote={onGetAQuote}
                        renderPlanCard={renderPlanCard}
                    />
                )
            }
            case PaymentSubscriptionUpdateStep.PLAN_CONFIRM: {
                return (
                    <>
                        <div className="flex flex-col items-center">
                            <p className="text-center text-xs">{t("confirm:payment_settings.upgrade.message.top")}</p>
                            <h2 className="text-center mt-20">{t("confirm:payment_settings.upgrade.message.bot")}</h2>
                        </div>
                        {planSelection && renderPlanCard(planSelection.plan, planSelection.price)}
                    </>
                )
            }
            case PaymentSubscriptionUpdateStep.UPDATE_FINISH: {
                return (
                    <Row justify="space-around">
                        <Col className="flex flex-col items-center">
                            <div className="relative flex items-center my-20">
                                {/* TODO FA-22: use correct theme color classes for icons */}
                                <CubeXLSuccessIcon className="absolute mx-auto inset-x-0 finway-primary-text" style={{ width: 230 }} />
                                <LinesIcon style={{ color: "#F3F3F3" }} />
                            </div>
                            <div className="flex flex-col items-center">
                                <h2 className="text-center my-20">{t("label:payment_settings.thx")}</h2>
                                <p className="text-center">{t("label:payment_settings.effective")}</p>
                            </div>
                        </Col>
                    </Row>
                )
            }
        }
    }

    return (
        <Modal
            title={t("action:payment_settings.upgrade_plan")}
            visible={isShowing}
            maskClosable={false}
            onCancel={onHide}
            footer={renderFooter()}
            destroyOnClose
            width={getWidth()}
        >
            {renderContent()}
        </Modal>
    )
}

export default PaymentSubscriptionUpdateModal
